import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface IndustryContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  primaryCta: string
  secondaryCta: string
  badges: string[]
  pillarsEyebrow: string
  pillarsTitle: string
  pillarsBody: string
  pillars: Pillar[]
  howEyebrow: string
  howTitle: string
  steps: Step[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', IndustryContent> = {
  en: {
    metaTitle: 'Identity & access for public sector',
    metaDescription:
      'Sovereign deployment, workforce + citizen identity, and audit-grade governance for government agencies and e-government services.',
    heroEyebrow: 'Industries · Public Sector',
    heroTitle: ['Sovereign identity.', 'Sovereign data.'],
    heroBody:
      'Self-hosted, jurisdiction-bound identity for government agencies, ministries, and e-government services. Citizen-facing and workforce identity on the same platform, with court-admissible evidence trails.',
    primaryCta: 'Start free trial →',
    secondaryCta: 'Talk with our experts',
    badges: [
      'Self-hosted, air-gap capable',
      'Sovereign data residency',
      'Trusted by IGA Istanbul Airport',
    ],
    pillarsEyebrow: 'What you get',
    pillarsTitle: 'What public-sector teams get with Monofor.',
    pillarsBody:
      'Government identity has constraints commercial identity does not: sovereignty, longevity, evidence integrity. Monofor was engineered for them.',
    pillars: [
      {
        title: 'Sovereign by default',
        description:
          'Self-host on your own infrastructure. Keep keys, audit trails, and recordings inside the jurisdiction you serve. No cross-border dependency.',
        bullets: [
          'Air-gapped and self-hosted deployment',
          'On-premises HSM and KMS integration',
          'No required external SaaS dependencies',
          'Operate fully offline if required',
        ],
      },
      {
        title: 'Citizen identity, agency identity, one platform',
        description:
          'Workforce, contractor, and citizen-facing identity on the same platform, with the same audit trail and the same residency posture.',
        bullets: [
          'Workforce and contractor identity',
          'Citizen-facing identity for e-government services',
          'Identity proofing and step-up flows',
          'Consent and data-subject rights workflows',
        ],
      },
      {
        title: 'Audit, evidence, and chain-of-custody',
        description:
          'Pre-built reports for national audit frameworks, ISO 27001, and ISO 27701. Tamper-evident logs with cryptographic chain-of-custody.',
        bullets: [
          'Cryptographic log integrity',
          'Long-retention evidence storage',
          'Court-admissible export bundles',
          'Configurable per-agency retention',
        ],
      },
      {
        title: 'Built for procurement reality',
        description:
          'Perpetual licensing, predictable per-identity pricing, and procurement documents that match the way your finance team actually buys.',
        bullets: [
          'Perpetual or annual licensing models',
          'Per-identity, not per-seat-per-app',
          'Procurement-ready documentation',
          'Local partner network for delivery',
        ],
      },
    ],
    howEyebrow: 'How it works',
    howTitle: 'Deploy. Federate. Defend.',
    steps: [
      {
        title: 'Deploy in your jurisdiction',
        description:
          'Self-host or operate inside a sovereign cloud. No data crosses borders unless you choose it to.',
      },
      {
        title: 'Federate citizen and workforce',
        description:
          'Bring agency staff, contractors, and citizen-facing services under one identity layer with the same auditing posture.',
      },
      {
        title: 'Operate, evidence, defend',
        description:
          'Pre-built reports and signed evidence packets give auditors and oversight bodies what they need without ad-hoc engineering.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      {
        label: 'Audit & compliance',
        description: 'Use case: making oversight bodies satisfied and engineers calm.',
        href: '/solutions/audit-compliance',
      },
      {
        label: 'Privileged session recording',
        description: 'Evidence backbone for sensitive-system access.',
        href: '/pam/session-recording',
      },
      {
        label: 'Identity governance',
        description: 'Entitlement mapping and review across agency systems.',
        href: '/sync/governance',
      },
    ],
  },
  tr: {
    metaTitle: 'Kamu için kimlik ve erişim',
    metaDescription:
      'Kamu kurumları ve e-devlet hizmetleri için egemen kurulum, çalışan + vatandaş kimliği ve denetim düzeyinde yönetişim.',
    heroEyebrow: 'Sektörler · Kamu',
    heroTitle: ['Egemen kimlik.', 'Egemen veri.'],
    heroBody:
      'Kamu kurumları, bakanlıklar ve e-devlet hizmetleri için kendi altyapınızda barındırılan, yargı bölgesine bağlı kimlik. Vatandaşa dönük ve çalışan kimliği aynı platformda, mahkemede delil niteliği taşıyan kanıt izleriyle.',
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Uzmanlarımızla görüşün',
    badges: [
      'Kendi altyapınızda, hava boşluklu çalışabilir',
      'Egemen veri yerleşimi',
      'İGA İstanbul Havalimanı’nın tercihi',
    ],
    pillarsEyebrow: 'Neler elde edersiniz',
    pillarsTitle: 'Kamu ekiplerinin Monofor ile elde ettikleri.',
    pillarsBody:
      'Kamu kimliğinin, ticari kimlikte olmayan kısıtları vardır: egemenlik, uzun ömür, kanıt bütünlüğü. Monofor bunlar için tasarlandı.',
    pillars: [
      {
        title: 'Varsayılan olarak egemen',
        description:
          'Kendi altyapınızda barındırın. Anahtarları, denetim izlerini ve kayıtları hizmet verdiğiniz yargı bölgesinin içinde tutun. Sınır ötesi bağımlılık yok.',
        bullets: [
          'Hava boşluklu ve kendi altyapınızda kurulum',
          'Şirket içi HSM ve KMS entegrasyonu',
          'Zorunlu harici SaaS bağımlılığı yok',
          'Gerekirse tamamen çevrimdışı çalışma',
        ],
      },
      {
        title: 'Vatandaş kimliği, kurum kimliği, tek platform',
        description:
          'Çalışan, yüklenici ve vatandaşa dönük kimlik aynı platformda, aynı denetim izi ve aynı veri yerleşimi duruşuyla.',
        bullets: [
          'Çalışan ve yüklenici kimliği',
          'E-devlet hizmetleri için vatandaşa dönük kimlik',
          'Kimlik doğrulama kanıtı ve kademeli doğrulama akışları',
          'Rıza ve ilgili kişi hakları iş akışları',
        ],
      },
      {
        title: 'Denetim, kanıt ve gözetim zinciri',
        description:
          'Ulusal denetim çerçeveleri, ISO 27001 ve ISO 27701 için hazır raporlar. Kriptografik gözetim zinciriyle kurcalamaya karşı korumalı loglar.',
        bullets: [
          'Kriptografik log bütünlüğü',
          'Uzun saklama süreli kanıt depolama',
          'Mahkemede delil niteliğinde dışa aktarım paketleri',
          'Kurum başına yapılandırılabilir saklama',
        ],
      },
      {
        title: 'Satın alma gerçekleri için tasarlandı',
        description:
          'Süresiz lisanslama, öngörülebilir kimlik başına fiyatlandırma ve finans ekibinizin gerçekten satın aldığı biçime uyan satın alma belgeleri.',
        bullets: [
          'Süresiz veya yıllık lisans modelleri',
          'Uygulama başına koltuk değil, kimlik başına',
          'Satın almaya hazır dokümantasyon',
          'Teslimat için yerel iş ortağı ağı',
        ],
      },
    ],
    howEyebrow: 'Nasıl çalışır',
    howTitle: 'Kurun. Federe edin. Savunun.',
    steps: [
      {
        title: 'Kendi yargı bölgenizde kurun',
        description:
          'Kendi altyapınızda barındırın veya egemen bir bulutta işletin. Siz seçmedikçe hiçbir veri sınır ötesine geçmez.',
      },
      {
        title: 'Vatandaş ve çalışanı federe edin',
        description:
          'Kurum personelini, yüklenicileri ve vatandaşa dönük hizmetleri aynı denetim duruşuna sahip tek kimlik katmanı altında toplayın.',
      },
      {
        title: 'İşletin, kanıtlayın, savunun',
        description:
          'Hazır raporlar ve imzalı kanıt paketleri; denetçilere ve gözetim organlarına ihtiyaç duyduklarını, plansız mühendislik gerektirmeden verir.',
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      {
        label: 'Denetim ve uyum',
        description: 'Kullanım senaryosu: gözetim organlarını memnun, mühendisleri sakin tutmak.',
        href: '/solutions/audit-compliance',
      },
      {
        label: 'Ayrıcalıklı oturum kaydı',
        description: 'Hassas sistem erişimi için kanıt omurgası.',
        href: '/pam/session-recording',
      },
      {
        label: 'Kimlik yönetişimi',
        description: 'Kurum sistemleri genelinde yetki eşleme ve gözden geçirme.',
        href: '/sync/governance',
      },
    ],
  },
}

type Props = { locale: string }

export default function PublicSectorPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/solutions/industries/public-sector"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: '/contact' }}
          badges={c.badges}
        />
        <SolutionPillars
          eyebrow={c.pillarsEyebrow}
          title={<>{c.pillarsTitle}</>}
          body={c.pillarsBody}
          pillars={c.pillars}
        />
        <ProductHowItWorks
          eyebrow={c.howEyebrow}
          title={<>{c.howTitle}</>}
          steps={c.steps}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
